import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

// APP-148 R89: "Not what you asked?" lives in the address (`?question=‹id›`), so a link opens it
// and Back closes it, as people expect of a dialog. Opened from a row, it's a step of its own and
// closing goes back to where it was; arrived at by a link, closing takes it out of the address in
// place. The award record (R115) and Done for you (R117) use it.

export const QUESTION_PARAM = 'question';
const Id = z.uuid();

/** What the address carries when the page opened the question itself. */
interface OpenedHere {
  question: string;
}

function openedHere(state: unknown, raw: string | null): boolean {
  return (
    raw !== null &&
    typeof state === 'object' &&
    state !== null &&
    (state as Partial<OpenedHere>).question === raw
  );
}

export interface QuestionAddress {
  /** `?question=` as it is, whatever it says. */
  raw: string | null;
  /** The row it names, when it names one. */
  questionId: string | null;
  /** Opens the question for a row: a new step, so Back closes it. */
  open: (id: string) => void;
  /** Closes it: back to before it opened, or out of the address when a link brought it. */
  close: () => void;
}

export function useQuestionAddress(): QuestionAddress {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const raw = params.get(QUESTION_PARAM);
  const questionId = raw !== null && Id.safeParse(raw).success ? raw : null;
  const here = openedHere(location.state, raw);
  return {
    raw,
    questionId,
    open: (id) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(QUESTION_PARAM, id);
          return next;
        },
        { state: { question: id } satisfies OpenedHere, preventScrollReset: true },
      );
    },
    close: () => {
      if (here) {
        void navigate(-1);
        return;
      }
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(QUESTION_PARAM);
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
    },
  };
}
